import React, { ForwardedRef, useEffect, useMemo } from "react";
import { useMeasure } from "react-use";
import { useLocation } from "react-router-dom";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { B2C_SITE_ORIGIN } from "@danishagro/shared/src/constants/urlRoot.constants";
import { getLoginLink } from "@danishagro/shared/src/helpers/getLoginLink.helper";
import { useBasket } from "@danishagro/shared/src/contexts/basket/basket.context";
import { useScreen } from "../../../../contexts/screen.context";
import { SiteHeaderPopUpType, useSiteHeader } from "../../../../contexts/siteHeader.context";
import { DA_Icon, DA_IconNames } from "../../../atoms/Icon/Icon.component";
import { DA_BasicLink } from "../../../atoms/BasicLink/BasicLink.component";
import { DA_Badge } from "../Badge/Badge.component";
import { useTranslations } from "../../../../contexts/translations/translations.context";
import { getRelativeRootOnLocalhost } from "../../../../helpers/getRelativeUrlOnLocalhost.helper";
import { useAppData } from "../../../../contexts/appData.context";
import S from "./ServiceButtonsB2B.module.scss";

export const DA_ServiceButtonsB2B = React.forwardRef((_, ref: ForwardedRef<HTMLDivElement>) => {
    const { pathname } = useLocation();

    const { getDictionaryString } = useTranslations();
    const { cvrAndCustomerNumbers, currentSite, displayFavoritesList, customerNumber } =
        useAppData();
    const { isMobileOrTablet } = useScreen();
    const {
        setCartButtonMeasure,
        setFarmInTimeButtonMeasure,
        setAccountButtonMeasure,
        showMiniCart,
        showAccountPicker,
        togglePopUp,
    } = useSiteHeader();
    const { orderLines, isLoadingBasket } = useBasket();
    const [cartButtonRef, cartButtonRect] = useMeasure<HTMLButtonElement>();

    const [accountButtonRef, accountButtonRect] = useMeasure<HTMLButtonElement>();

    const totalQuantity = useMemo(() => {
        if (orderLines?.[0]) {
            return orderLines.reduce((total, line) => total + line.quantity, 0);
        }

        return 0;
    }, [orderLines]);

    const favoritesListUrl = `${getRelativeRootOnLocalhost(B2C_SITE_ORIGIN)}/favorites`;

    const isFavoritesActive = pathname === "/favorites";

    useEffect(() => {
        setCartButtonMeasure(cartButtonRect);
        setAccountButtonMeasure(accountButtonRect);

        // use cartbutton to calc account popup
        setAccountButtonMeasure(cartButtonRect);
    }, [
        cartButtonRect,
        setCartButtonMeasure,
        setFarmInTimeButtonMeasure,
        setAccountButtonMeasure,
        accountButtonRect,
    ]);

    return (
        <div className={S.serviceButtons} ref={ref}>
            {/** Cart */}

            <button
                type="button"
                className={S.serviceButton}
                onClick={() => togglePopUp(SiteHeaderPopUpType.MiniCart)}
                ref={cartButtonRef}
                aria-label={getDictionaryString("accessibilityButtonCart")}
            >
                <span className={cn(S.serviceButton, showMiniCart && S.active)}>
                    <DA_Icon name={DA_IconNames.Cart} className={S.icon} />

                    {(totalQuantity > 0 || isLoadingBasket) && (
                        <DA_Badge
                            size="small"
                            text={totalQuantity > 99 ? "99+" : totalQuantity}
                            className={S.buttonBadge}
                            showSpinner={isLoadingBasket}
                        />
                    )}
                </span>
            </button>
            {/** Favourties list */}
            {displayFavoritesList && (
                <DA_BasicLink
                    href={favoritesListUrl}
                    target="_self"
                    className={cn(S.serviceButton, S.favServiceBtn, isFavoritesActive && S.active)}
                    ariaLabel={getDictionaryString("accessibilityFarmInTimeLinkText")}
                >
                    <DA_Icon name={DA_IconNames.HeartOutline} className={S.icon} />
                </DA_BasicLink>
            )}

            {/** Account: Logged Out */}
            {!globalThis.isAuthenticated && (
                <DA_BasicLink
                    href={getLoginLink(currentSite)}
                    target="_self"
                    className={cn(S.serviceButton, S.user)}
                    ariaLabel={getDictionaryString("accessibilityMobileLogin")}
                >
                    <div className={S.buttonIconWrapper}>
                        <DA_Icon name={DA_IconNames.UserLoggedOut} className={S.icon} />
                    </div>
                    {getDictionaryString("loginTitle")}
                </DA_BasicLink>
            )}

            {/** Account: Logged In */}
            {!isMobileOrTablet && globalThis.isAuthenticated ? (
                <button
                    type="button"
                    onClick={() => togglePopUp(SiteHeaderPopUpType.AccountPicker)}
                    className={cn(S.serviceButton, S.user, showAccountPicker && S.active)}
                    ref={accountButtonRef}
                    title={getDictionaryString("account picker")}
                >
                    <div className={S.buttonIconWrapper}>
                        <DA_Icon name={DA_IconNames.UserLoggedIn} className={S.icon} />
                    </div>
                    {cvrAndCustomerNumbers.firstName} {cvrAndCustomerNumbers.lastName}
                    {customerNumber && ` (${customerNumber})`}
                </button>
            ) : null}
        </div>
    );
});

DA_ServiceButtonsB2B.displayName = "DA_ServiceButtonsB2B";
